import { useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";

import { CheckboxGroup, Span } from "@icg360/design-system";

import { MultiStepFormLayout } from "components/multi-step-form-layout";
import { useSelectedPolicy } from "data/ss-data-hooks";
import { useMultiStepFormState } from "hooks/use-multi-step-form-state";

import { TingContext, type TingContextState } from "./ting-flow";
import styles from "./ting-offer.module.scss";
import {
  formatTingPolicyCardStreet,
  formatTingPolicyCardSubtitle,
  tingPolicyCardUiIcon,
} from "./ting-policy-helpers";

const STEP_LABELS = [
  "Select properties",
  "Confirm details",
  "Review and finish",
];

export const PolicyConfirmation = () => {
  const { data: selectedPolicy } = useSelectedPolicy();
  const { state, setFields, formNavigate } =
    useMultiStepFormState<TingContextState>(TingContext);
  const defaultsAppliedRef = useRef(false);
  const sessionPolicyId = selectedPolicy?.keystonePolicyId;
  const eligibleTingPolicies = state?.eligibleTingPolicies;
  const tingEligiblePolicies = eligibleTingPolicies ?? [];

  useEffect(() => {
    const list = eligibleTingPolicies ?? [];
    if (!list.length || defaultsAppliedRef.current) {
      return;
    }
    if (state?.selectedTingPolicyIds !== undefined) {
      defaultsAppliedRef.current = true;
      return;
    }
    defaultsAppliedRef.current = true;
    const sessionInList = list.some(
      (p) => p.keystonePolicyId === sessionPolicyId,
    );
    const nextIds =
      list.length === 1
        ? [list[0].keystonePolicyId]
        : sessionInList && sessionPolicyId
          ? [sessionPolicyId]
          : [];
    setFields({ selectedTingPolicyIds: nextIds });
  }, [
    sessionPolicyId,
    setFields,
    state?.selectedTingPolicyIds,
    eligibleTingPolicies,
  ]);

  const selectedIds = state?.selectedTingPolicyIds ?? [];

  if (!tingEligiblePolicies.length) {
    return <Navigate to="/my/property-services/ting-offer" replace />;
  }

  if (tingEligiblePolicies.length === 1) {
    return <Navigate to="../contact-information" replace />;
  }

  const onSelectionChange = (ids: string[]) => {
    setFields({ selectedTingPolicyIds: ids });
  };

  return (
    <MultiStepFormLayout
      showLogo
      labels={STEP_LABELS}
      step={1}
      totalSteps={3}
      heading="Choose which properties you want to enroll in Ting"
      buttonProps={{
        primary: {
          path: "../contact-information",
          disabled: selectedIds.length === 0,
        },
        cancel: { path: "/my/property-services/ting-offer" },
        promptOnCancel: true,
        formNavigate,
      }}
    >
      <CheckboxGroup
        className={styles.tingPropertyCheckboxGroup}
        value={selectedIds}
        onChange={onSelectionChange}
        aria-label="Properties to receive a Ting device"
      >
        {tingEligiblePolicies.map((policy) => (
          <CheckboxGroup.Card
            className={styles.tingPropertyCheckboxCard}
            key={policy.keystonePolicyId}
            value={policy.keystonePolicyId}
            uiIcon={tingPolicyCardUiIcon(policy)}
            title={formatTingPolicyCardStreet(policy)}
            description={
              <Span color="quiet" size="sm">
                {formatTingPolicyCardSubtitle(policy)}
              </Span>
            }
            data-testid={`ting-offer-property-${policy.keystonePolicyId}`}
          />
        ))}
      </CheckboxGroup>
      <div className={styles.tingPropertyDisclaimer}>
        <Span size="md" color="quiet">
          Only properties that are eligible to receive a Ting sensor will appear
          in the list above.
        </Span>
      </div>
    </MultiStepFormLayout>
  );
};
